import React from 'react';
import isString from 'lodash/isString';
import momentTimezone from 'moment-timezone';

import gettext from '@eb/gettext';
import { getAnalyticsClickHandler } from '@eb/site-analytics';
import { lazyGettext } from '@eb/lazy-gettext';

import { ANCHOR_LINK, DIVIDER } from '@eb/eds-nav-list-item';
import { Icon } from '@eb/eds-icon';
import { Link } from '@eb/eds-link';
import { PhoneChunky } from '@eb/eds-iconography';

import {
    ACTION_CREATE_ATTEMPT,
    ACTION_DISCOVER,
    ACTION_HELP_ARTICLE,
    ACTION_HELP_LINK_CLICK,
    ACTION_HELP_LINK_HOVER,
    ACTION_HOST_AN_EVENT_CLICK,
    ACTION_HOST_AN_EVENT_HOVER,
    ACTION_LOGIN_ATTEMPT,
    ACTION_LOGIN_CLICK,
    ACTION_LOGIN_HOVER,
    ACTION_LOGO_HOME,
    ACTION_PHONE_SUPPORT_CLICK,
    CATEGORY_HEADER,
    LABEL_HELP_CENTER,
    LABEL_CREATE_EVENT,
    PHONE_SUPPORT_NUMBER,
    PHONE_SUPPORT_START_TIME,
    PHONE_SUPPORT_END_TIME,
    PHONE_SUPPORT_START_TIME_FORMAT,
    PHONE_SUPPORT_END_TIME_FORMAT,
    PHONE_SUPPORT_DEFAULT_TIMEZONE,
} from '../constants';
import { getUserMenuItemsLookup } from './user';

// #3D64FF is the hex value for $ui-blue, the standard blue used for links at EB
export const UI_BLUE = '#3D64FF';

export const isHomepage = (requestPath) => requestPath === '/';

/**
 * Generates data needed to pass as `logoInfo` prop to EDS Global Header
 * @param {string} url URL to navigate to when logo is clicked
 * @param {string} requestPath Current request path (for analytics)
 * @param {object} [logoOptions={}] Additional options matching the EDS Global Header `logoInfo` prop
 * @returns {object}
 */
export const getLogoInfo = (url, requestPath, logoOptions = {}) => ({
    url,

    onClick: getAnalyticsClickHandler({
        requestPath,
        action: ACTION_LOGO_HOME,
        category: CATEGORY_HEADER,
    }),

    ...logoOptions,
});

const knownOrganizerManageUrlPathsRegexp = /^\/(affiliates|facebook-publish|links|manage\/events|myevent|organizations|questions)/;

/**
 * Generates a link for the logo based on the current context.
 *
 * Generally, this helps Music customers get back to their events listing
 * rather than the consumer homepage.
 *
 * @param {string} user         The currently logged-in user
 * @param {string} fallbackUrl  The original URL to fall back on
 * @param {string} requestPath  The current path the user is navigating from
 */
export const getLogoInfoUrl = (user, fallbackUrl, requestPath) => {
    if (
        user &&
        user.usesNewPermissions &&
        knownOrganizerManageUrlPathsRegexp.test(requestPath)
    ) {
        return '/organizations/events';
    }

    return fallbackUrl;
};

/**
 * Retrieves list of quick links to pass to `quickLinks` prop of EDS Global Header
 * @param {string} serverUrl Base site URL (e.g. "http://www.eventbrite.com")
 * @param {string} ebDomain Current site domain (for Janus checkpoint)
 * @param {string} requestPath Current request path (for analytics)
 * @returns {object[]}
 */
export const getQuickLinks = (
    serverUrl,
    ebDomain,
    requestPath,
    launchNewNavigationHeader = false,
) => {
    let quickLinks = [];
    // for non-search pages (including "/events/" city browse pages),
    // show a link to lead the user to the search page
    if (isString(requestPath) && !requestPath.match('^/d/(?!.*/events/)')) {
        quickLinks = [
            ...quickLinks,
            {
                url: `${serverUrl}/d/local/all-events/`,
                content: gettext('Browse Events'),

                onClick: getAnalyticsClickHandler(
                    {
                        requestPath,
                        action: ACTION_DISCOVER,
                        category: CATEGORY_HEADER,
                    },
                    {
                        checkpointName: 'discover_header_link_clicked',
                        domain: ebDomain,
                    },
                ),
            },
        ];
    }

    if (launchNewNavigationHeader) {
        // Here we add additional links to the small screen dropdown in the new header. When we add {display: 'none'}
        // as a style it hides the links on large screen sizes and shows them in the small screen header.
        quickLinks = [
            ...quickLinks,
            {
                url: `${serverUrl}/organizer/overview/`,
                content: gettext('Host an event'),
                style: { display: 'none' },
                onClick: getAnalyticsClickHandler({
                    requestPath,
                    action: ACTION_HOST_AN_EVENT_CLICK,
                    category: CATEGORY_HEADER,
                    label: 'HostAnEvent',
                }),
                onMouseLeave: getAnalyticsClickHandler({
                    requestPath,
                    action: ACTION_HOST_AN_EVENT_HOVER,
                    category: CATEGORY_HEADER,
                    label: 'HostAnEvent',
                }),
            },
            {
                url: `${serverUrl}/support/`,
                content: gettext('Help'),
                style: { display: 'none' },
                onClick: getAnalyticsClickHandler({
                    requestPath,
                    action: ACTION_HELP_LINK_CLICK,
                    category: CATEGORY_HEADER,
                    label: 'Help',
                }),
                onMouseLeave: getAnalyticsClickHandler({
                    requestPath,
                    action: ACTION_HELP_LINK_HOVER,
                    category: CATEGORY_HEADER,
                    label: 'Help',
                }),
            },
            {
                url: `${serverUrl}/create/`,
                content: gettext('Create an event'),
                style: { display: 'none' },
                onClick: getAnalyticsClickHandler({
                    requestPath,
                    action: ACTION_HOST_AN_EVENT_CLICK,
                    category: CATEGORY_HEADER,
                    label: LABEL_CREATE_EVENT,
                }),
                onMouseLeave: getAnalyticsClickHandler({
                    requestPath,
                    action: ACTION_HOST_AN_EVENT_HOVER,
                    category: CATEGORY_HEADER,
                    label: LABEL_CREATE_EVENT,
                }),
            },
        ];
    }

    return quickLinks;
};

/**
 * Gets the info for displaying create event CTA to be passed to `callToActionInfo` prop of EDS Global Header
 * @param {string} serverUrl Base site URL (e.g. "http://www.eventbrite.com")
 * @param {string} ebDomain Current site domain (for Janus checkpoint)
 * @param {string} requestPath Current request path (for analytics)
 * @param {boolean} isAuthenticated Whether or not the user is authenticated
 * @param {boolean} canCreateEvents Whether or not the user can create events
 * @param {React.ReactNode} [content='Create Event'] The content of the CTA
 * @param {object} [dimensions={}] Additional GA dimensions for when the CTA is clicked
 * @returns {object}
 */
export const getCreateEventCTA = (
    serverUrl,
    ebDomain,
    requestPath,
    isAuthenticated,
    canCreateEvents,
    launchNewNavigationHeader = false,
    content = lazyGettext('Create Event'),
    dimensions = {},
) => {
    let showCta;
    // If the launchNewNavigationHeader feature flag is off, continue to show the CTA as it was shown before
    if (!launchNewNavigationHeader) {
        showCta = canCreateEvents || isHomepage(requestPath);
    } else {
        // if the launchNewNavigationHeader feature flag is on, show the CTA if the user can create events AND is on the
        // homepage or a /d/ page
        showCta =
            canCreateEvents &&
            (isHomepage(requestPath) || requestPath.match('^/d/.*'));
    }
    let callToAction;
    const action = launchNewNavigationHeader
        ? ACTION_HOST_AN_EVENT_CLICK
        : ACTION_CREATE_ATTEMPT;
    const label = launchNewNavigationHeader ? LABEL_CREATE_EVENT : '';

    if (showCta) {
        let dataAttributes = {
            'data-automation': 'global-nav-create',
            onClick: getAnalyticsClickHandler(
                {
                    requestPath,
                    action,
                    category: CATEGORY_HEADER,
                    label,
                    dimensions,
                },
                {
                    checkpointName: 'hiw_global_header_cta_clicked',
                    pageWillChange: isAuthenticated,
                    domain: ebDomain,
                },
            ),
        };
        if (launchNewNavigationHeader) {
            dataAttributes = {
                ...dataAttributes,
                onMouseLeave: getAnalyticsClickHandler({
                    requestPath,
                    action: ACTION_HOST_AN_EVENT_HOVER,
                    category: CATEGORY_HEADER,
                    label: LABEL_CREATE_EVENT,
                }),
            };
        }

        callToAction = {
            url: `${serverUrl}/create/`,
            content,
            ...dataAttributes,
        };
    }

    return callToAction;
};

/**
 * @typedef SearchSuggestion
 * @property {string} value
 * @property {string} content
 */

/**
 * Gets the search information passed to the `searchInfo` prop of EDS Global Header
 * @param {string} serverUrl Base site URL (e.g. "http://www.eventbrite.com")
 * @param {string} query Current search query
 * @param {SearchSuggestion[]} suggestions Search suggestions for auto-complete
 * @param {boolean} hideSearchBar Whether or not to hide the search bar
 * @returns {object}
 */
export const getSearchInfo = (serverUrl, query, suggestions, hideSearchBar) => {
    let searchInfo;

    if (!hideSearchBar) {
        searchInfo = {
            formAction: `${serverUrl}/d/`,
            label: gettext('Search for events'),
            query,
            suggestions,
        };
    }
    return searchInfo;
};

/**
 * Gets list of links usually passed as `nonUserQuickLinks` prop to EDS Global Header
 * @param {string} requestPath Current request path (for analytics)
 * @param {boolean} isAuthenticated Whether or not the user is authenticated
 * @param {string} loginUrl URL to the sign-in page
 * @returns object[]
 */
export const getNonUserQuickLinks = (
    requestPath,
    isAuthenticated,
    loginUrl,
    launchNewNavigationHeader = false,
) => {
    if (isAuthenticated) {
        return null;
    }
    const action = launchNewNavigationHeader
        ? ACTION_LOGIN_CLICK
        : ACTION_LOGIN_ATTEMPT;
    let link = {
        url: loginUrl,
        content: gettext('Sign In'),
        onClick: getAnalyticsClickHandler({
            requestPath,
            action,
            category: CATEGORY_HEADER,
        }),
    };
    if (launchNewNavigationHeader) {
        link = {
            ...link,
            onMouseLeave: getAnalyticsClickHandler({
                requestPath,
                action: ACTION_LOGIN_HOVER,
                category: CATEGORY_HEADER,
            }),
        };
    }

    return [link];
};

const _getTimezoneAbbreviation = () => {
    const timeZone = momentTimezone.tz.guess();
    const time = new Date();
    const timeZoneOffset = time.getTimezoneOffset();

    return momentTimezone.tz.zone(timeZone).abbr(timeZoneOffset);
};

const _getPhoneSupportDisplayTime = (time, format) => {
    const userTimezone = momentTimezone.tz.guess();
    const defaultTimeWithTimezone = momentTimezone.tz(
        time,
        format,
        PHONE_SUPPORT_DEFAULT_TIMEZONE,
    );

    return defaultTimeWithTimezone.clone().tz(userTimezone).format(format);
};

const _getAvailableHoursDisplay = () => {
    const startTime = _getPhoneSupportDisplayTime(
        PHONE_SUPPORT_START_TIME,
        PHONE_SUPPORT_START_TIME_FORMAT,
    );
    const endTime = _getPhoneSupportDisplayTime(
        PHONE_SUPPORT_END_TIME,
        PHONE_SUPPORT_END_TIME_FORMAT,
    );
    const timezone = _getTimezoneAbbreviation();

    return `${startTime} - ${endTime} ${timezone}`;
};

const _isWeekend = (dateNow) => {
    const day = dateNow.day();

    return day === 6 || day === 0;
};

const _isPhoneSupportOnline = () => {
    // users local time transposed to the default timezone
    // ex: 6am PST => 8am CST
    const localTimeTransposed = momentTimezone.tz(
        momentTimezone(),
        PHONE_SUPPORT_DEFAULT_TIMEZONE,
    );
    let isOnline = false;

    if (_isWeekend(localTimeTransposed)) {
        return isOnline;
    }

    const beforeTime = momentTimezone(
        PHONE_SUPPORT_START_TIME,
        PHONE_SUPPORT_START_TIME_FORMAT,
    );
    const afterTime = momentTimezone(
        PHONE_SUPPORT_END_TIME,
        PHONE_SUPPORT_END_TIME_FORMAT,
    );

    if (localTimeTransposed.isBetween(beforeTime, afterTime)) {
        isOnline = true;
    }

    return isOnline;
};

const _getPhoneSupportLink = (requestPath, isSuperCreator) => {
    if (!isSuperCreator) {
        return [];
    }

    let phoneSupportLink = (
        <p className="eds-text-color--ui-500">{`${PHONE_SUPPORT_NUMBER} (offline)`}</p>
    );

    if (_isPhoneSupportOnline()) {
        phoneSupportLink = (
            <Link to={`tel:${PHONE_SUPPORT_NUMBER}`} style="inverse">
                {PHONE_SUPPORT_NUMBER}
            </Link>
        );
    }

    return [
        { type: DIVIDER },
        {
            type: ANCHOR_LINK,
            content: (
                <div className="eds-align--space-around">
                    <div className="eds-l-mar-right-4">
                        <Icon type={<PhoneChunky />} />
                    </div>
                    <div>
                        <div className="eds-l-mar-bot-1">
                            {phoneSupportLink}
                        </div>
                        <p>Monday - Friday</p>
                        <p>{_getAvailableHoursDisplay()}</p>
                    </div>
                </div>
            ),
            target: '_blank',
            rel: 'noopener noreferrer',

            onClick: getAnalyticsClickHandler({
                requestPath,
                action: ACTION_PHONE_SUPPORT_CLICK,
                category: CATEGORY_HEADER,
                label: LABEL_HELP_CENTER,
            }),
        },
    ];
};

/**
 * @typedef {object} UrlInfo
 * @property {string} url Help URL
 * @property {string} label Display label
 * @property {string} gaLabel Google Analytics label
 */

/**
 * Retrieves the help menu info typically passed to the `additionalMenus` prop of the EDS Global Header
 * @param {object} helpUrls
 * @param {string} helpUrls.home
 * @param {UrlInfo[]} helpUrls.suggested
 * @param {string} requestPath Current request path (for analytics)
 * @returns object
 */
export const getHelpMenu = (
    { home, suggested },
    requestPath,
    isSuperCreator = false,
    launchNewNavigationHeader = false,
) => {
    const click_action = launchNewNavigationHeader
        ? ACTION_HELP_LINK_CLICK
        : ACTION_HELP_ARTICLE;

    const suggestedHelpMenuItems = suggested.map(({ url, label, gaLabel }) => {
        let link = {
            type: ANCHOR_LINK,
            path: url,
            content: label,
            onClick: getAnalyticsClickHandler({
                requestPath,
                action: click_action,
                category: CATEGORY_HEADER,
                label: gaLabel,
            }),
        };
        // In the new navigation header, fire a GA action when a user hovers over a link, leaves, and doesn't
        // click it
        if (launchNewNavigationHeader) {
            link = {
                ...link,
                onMouseLeave: getAnalyticsClickHandler({
                    category: CATEGORY_HEADER,
                    action: ACTION_HELP_LINK_HOVER,
                    label: gaLabel,
                }),
            };
        }
        return link;
    });
    const phoneSupportLink = isSuperCreator
        ? _getPhoneSupportLink(requestPath, isSuperCreator)
        : [];
    const additionalHelpMenuItems = [...phoneSupportLink];
    const helpCenterItem = launchNewNavigationHeader
        ? []
        : [
              { type: DIVIDER },
              {
                  type: ANCHOR_LINK,
                  path: home,
                  content: gettext('Help Center'),
                  target: '_blank',
                  rel: 'noopener noreferrer',

                  onClick: getAnalyticsClickHandler({
                      requestPath,
                      action: ACTION_HELP_ARTICLE,
                      category: CATEGORY_HEADER,
                      label: LABEL_HELP_CENTER,
                  }),
              },
          ];
    const navItems = [
        ...suggestedHelpMenuItems,
        ...helpCenterItem,
        ...additionalHelpMenuItems,
    ];

    return {
        label: gettext('Help'),
        fallbackUrl: home,
        navItems,
    };
};

/**
 * Gets info to generate Organizer menu item usually passed `additionalMenus` prop of EDS Global Header
 * @param {object} organizeUrls
 * @param {UrlInfo} organizeUrls.home
 * @param {UrlInfo[]} organizeUrls.suggested
 * @param {string} requestPath Current request path (for analytics)
 * @returns {object}
 */
export const getOrganizeMenu = (
    { home: { url: homeUrl }, suggested },
    requestPath,
    launchNewNavigationHeader = false,
) => {
    const click_action = launchNewNavigationHeader
        ? ACTION_HOST_AN_EVENT_CLICK
        : '';
    const suggestedOrganizeMenuItems = suggested.map(
        ({ url, gaLabel, label, accentLink = false }) => {
            // UI_BLUE is the hex value for $ui-blue, the standard blue used for links at EB
            const style = accentLink ? { color: UI_BLUE } : {};
            let link = {
                type: ANCHOR_LINK,
                path: url,
                style,
                content: label,
                onClick: getAnalyticsClickHandler({
                    requestPath,
                    action: click_action,
                    category: CATEGORY_HEADER,
                    label: gaLabel,
                }),
            };
            // In the new navigation header, fire a GA action when a user hovers over a link, leaves, and doesn't
            // click it
            if (launchNewNavigationHeader) {
                link = {
                    ...link,
                    onMouseLeave: getAnalyticsClickHandler({
                        action: ACTION_HOST_AN_EVENT_HOVER,
                        category: CATEGORY_HEADER,
                        label: gaLabel,
                    }),
                };
            }
            return link;
        },
    );
    const navItems = [...suggestedOrganizeMenuItems];

    const label = launchNewNavigationHeader
        ? gettext('Host an event')
        : gettext('Organize');

    return {
        label,
        fallbackUrl: homeUrl,
        navItems,
    };
};

/**
 * Retrieves the list of staff-related user menu items given the specified configurations
 * @param {object} options
 * @param {object} options.userMenuItemsLookup Lookup of all user menu items
 * @param {boolean} options.isStaff Whether or not the account is a staff account
 * @param {boolean} options.hasTikibar Whether or not the account has access to Tikibar
 * @returns {object[]}
 */
export const getStaffUserMenuItems = ({
    userMenuItemsLookup,
    isStaff,
    hasTikibar,
}) => {
    if (!isStaff) {
        return [];
    }

    const { admin, tikibar, featureFlags, experiments } = userMenuItemsLookup;
    const menuItems = [admin];

    if (hasTikibar) {
        menuItems.push(tikibar);
    }

    menuItems.push(featureFlags, experiments, { type: DIVIDER });

    return menuItems;
};

/**
 * Retrieves the list of attendee-related user menu items given the specified configurations
 * @param {object} options
 * @param {object} options.userMenuItemsLookup Lookup of all user menu items
 * @param {number} [options.numFollowing] How many accounts the user is following
 * @param {boolean} options.canUseCredits Whether or not the user has access to credits
 * @param {boolean} options.showBrowseEvents Whether or not to show the "Browse events" menu item
 * @returns {object[]}
 */
export const getAttendeeUserMenuItems = ({
    userMenuItemsLookup,
    numFollowing,
    canUseCredits,
    showBrowseEvents,
}) => {
    const {
        browseEvents,
        tickets,
        credits,
        saved,
        following,
        collections,
        interests,
    } = userMenuItemsLookup;
    const attendeeItems = [];

    if (showBrowseEvents) {
        attendeeItems.push(browseEvents, { type: DIVIDER });
    }

    attendeeItems.push(tickets);

    if (canUseCredits) {
        attendeeItems.push(credits);
    }

    attendeeItems.push(saved);
    attendeeItems.push(following);
    attendeeItems.push(collections);
    attendeeItems.push(interests);

    return attendeeItems;
};

const _getOrganizerMenuItems = ({
    canInvite,
    canUseSponsorships,
    canUseStudio,
    hasContactsPermission,
    hasEvents,
    hasOrganizers,
    isAttendee,
    userMenuItemsLookup,
}) => {
    if (isAttendee) {
        return [];
    }

    const {
        manageEvents,
        organizerProfile,
        contacts,
        studio,
        sponsorships,
    } = userMenuItemsLookup;
    const menuItems = [{ type: DIVIDER }, manageEvents];

    if (hasOrganizers) {
        menuItems.push(organizerProfile);
    }
    if (canInvite && (hasContactsPermission || hasEvents)) {
        menuItems.push(contacts);
    }
    if (canUseStudio) {
        menuItems.push(studio);
    }
    if (canUseSponsorships) {
        menuItems.push(sponsorships);
    }

    return menuItems;
};

export const _getCreateEventMenuItems = ({
    userMenuItemsLookup,
    canCreateEvents,
}) => {
    if (!canCreateEvents) {
        return [];
    }

    return [userMenuItemsLookup.create];
};

const _getUserContentMenuItems = (userMenuItemsLookup) => [
    userMenuItemsLookup.accountSettingsWithIcon,
    { type: DIVIDER },
];

const _getUserMenuItems = ({
    adminServerUrl,
    canCreateEvents,
    canInvite,
    canUseSponsorships,
    canUseStudio,
    canUseCredits,
    hasContactsPermission,
    hasEvents,
    hasOrganizers,
    hasTikibar,
    isAttendee,
    isStaff,
    logoutUrl,
    numOrders,
    numSavedEvents,
    numFollowing,
    publicId,
    requestPath,
    serverUrl,
    accountSettingsPath,
    showBrowseEvents,
    email,
    fullName,
    userImageUrl,
    isManageOverviewActive,
}) => {
    const userMenuItemsLookup = getUserMenuItemsLookup({
        adminServerUrl,
        logoutUrl,
        numOrders,
        numSavedEvents,
        numFollowing,
        publicId,
        requestPath,
        serverUrl,
        accountSettingsPath,
        email,
        fullName,
        userImageUrl,
        isManageOverviewActive,
    });
    const userMenuItems = _getUserContentMenuItems(userMenuItemsLookup);
    const staffUserMenuItems = getStaffUserMenuItems({
        userMenuItemsLookup,
        hasTikibar,
        isStaff,
    });
    const attendeeMenuItems = getAttendeeUserMenuItems({
        userMenuItemsLookup,
        numFollowing,
        canUseCredits,
        showBrowseEvents,
    });
    const organizerMenuItems = _getOrganizerMenuItems({
        userMenuItemsLookup,
        canInvite,
        hasContactsPermission,
        hasEvents,
        hasOrganizers,
        isAttendee,
        canUseSponsorships,
        canUseStudio,
    });
    const createEventMenuItems = _getCreateEventMenuItems({
        userMenuItemsLookup,
        canCreateEvents,
    });

    return [
        ...userMenuItems,
        ...staffUserMenuItems,
        ...attendeeMenuItems,
        ...organizerMenuItems,
        { type: DIVIDER },
        userMenuItemsLookup.accountSettings,
        ...createEventMenuItems,
        userMenuItemsLookup.logout,
    ];
};

/**
 * Get info for the user menu typically passed to the `userMenuInfo` prop of EDS Global Header
 * @param {object} options
 * @param {string} options.serverUrl Base site URL (e.g. "http://www.eventbrite.com")
 * @param {string} options.adminServerUrl Base Admin site URL (e.gettext. "http://admin.eventbrite.com")
 * @param {string} options.logoutUrl URL to logout page
 * @param {string} options.requestPath Current request path (for analytics)
 * @param {object} options.user User object
 * @param {boolean} options.showBrowseEvents Whether or not to include Browse Events menu item
 * @param {boolean} options.isManageOverviewActive Whether or not the new Manage Overview page is active
 * @param {string} [options.iconColor] Color of the user icon
 */
export const getUserMenuInfo = ({
    serverUrl,
    adminServerUrl,
    logoutUrl,
    requestPath,
    user: {
        canCreateEvents,
        canInvite,
        canUseSponsorships,
        canUseStudio,
        firstName,
        hasContactsPermission,
        hasEvents,
        hasOrganizers,
        hasTikibar,
        isAttendee,
        isAuthenticated,
        isStaff,
        numOrders,
        numSavedEvents,
        numFollowing,
        publicId,
        thumbnailUrl,
        fullName,
        email,
    },
    showBrowseEvents,
    isManageOverviewActive,
    iconColor = undefined,
    featureFlags = {},
}) => {
    if (!isAuthenticated) {
        return null;
    }

    const { launchCreatorCreditsSectionAttendee: canUseCredits } = featureFlags;

    const accountSettingsPath = 'account-settings';

    return {
        iconColor,
        label: firstName,
        fallbackUrl: `${serverUrl}/${accountSettingsPath}/`,
        imageUrl: thumbnailUrl,

        navItems: _getUserMenuItems({
            adminServerUrl,
            canCreateEvents,
            canInvite,
            canUseSponsorships,
            canUseStudio,
            canUseCredits,
            hasContactsPermission,
            hasEvents,
            hasOrganizers,
            hasTikibar,
            isAttendee,
            isStaff,
            logoutUrl,
            numOrders,
            numSavedEvents,
            numFollowing,
            publicId,
            requestPath,
            serverUrl,
            accountSettingsPath,
            showBrowseEvents,
            fullName,
            email,
            userImageUrl: thumbnailUrl,
            isManageOverviewActive,
        }),
    };
};
